/**
 * Search index loading + matching.
 *
 * The whole index (upcoming departures, trip pages, pages, blog posts) is
 * fetched once from /wp-json/tsa/v1/search-index and then filtered in the
 * browser, so typing never waits on the network. It is kept in sessionStorage
 * keyed by the index version PHP bumps whenever indexed content is saved.
 */

const STORAGE_PREFIX = 'tsaSearchIndex:';

let loading = null;
let cache = null;

/**
 * @param {Object} config TSA_CHROME.search
 * @return {Promise<Object>} the index
 */
export function loadIndex(config) {
    if (cache) {
        return Promise.resolve(cache);
    }

    if (loading) {
        return loading;
    }

    const key = STORAGE_PREFIX + config.version;

    try {
        const stored = window.sessionStorage.getItem(key);
        if (stored) {
            cache = JSON.parse(stored);
            return Promise.resolve(cache);
        }
    } catch (e) {
        /* private mode / quota — fall through to the network */
    }

    loading = fetch(config.index, { credentials: 'same-origin' })
        .then((response) => {
            if (!response.ok) {
                throw new Error('search index request failed');
            }
            return response.json();
        })
        .then((index) => {
            cache = index;
            try {
                // Drop other versions so the quota isn't filled with stale copies.
                Object.keys(window.sessionStorage)
                    .filter((k) => k.indexOf(STORAGE_PREFIX) === 0 && k !== key)
                    .forEach((k) => window.sessionStorage.removeItem(k));
                window.sessionStorage.setItem(key, JSON.stringify(index));
            } catch (e) {
                /* not fatal — the in-memory copy is enough for this page view */
            }
            return index;
        })
        .finally(() => {
            loading = null;
        });

    return loading;
}

/**
 * Lowercase, strip accents and punctuation so "Kosciuszko," matches "kosciuszko".
 *
 * @param {string} value
 * @return {string}
 */
function normalise(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Score one entry against the search terms. Returns 0 when a term is missing
 * entirely, so every word the visitor typed has to appear somewhere.
 *
 * @param {Object}   item
 * @param {string[]} terms
 * @return {number}
 */
function score(item, terms) {
    const title = normalise(item.t);
    const extra = normalise([item.ty, item.l, item.sh ? 'short hike' : ''].filter(Boolean).join(' '));
    const words = title.split(' ');

    let total = 0;

    for (let i = 0; i < terms.length; i++) {
        const term = terms[i];
        let best = 0;

        if (title === term) {
            best = 100;
        } else if (title.indexOf(term) === 0) {
            best = 70;
        } else if (words.some((word) => word.indexOf(term) === 0)) {
            best = 50;
        } else if (title.indexOf(term) !== -1) {
            best = 30;
        } else if (extra.indexOf(term) !== -1) {
            best = 15;
        }

        if (!best) {
            return 0;
        }

        total += best;
    }

    return total;
}

const GROUPS = [
    { key: 'event', title: 'Upcoming departures', limit: 6 },
    { key: 'trip', title: 'Trips & destinations', limit: 4 },
    { key: 'page', title: 'Pages', limit: 3 },
    { key: 'post', title: 'Stories & news', limit: 3 },
];

/**
 * @param {Object} index
 * @param {string} query
 * @return {{groups: Array, total: number}}
 */
export function searchIndex(index, query) {
    const terms = normalise(query).split(' ').filter(Boolean);

    if (!index || !index.items || !terms.length) {
        return { groups: [], total: 0 };
    }

    const scored = [];

    for (let i = 0; i < index.items.length; i++) {
        const item = index.items[i];
        const value = score(item, terms);
        if (value > 0) {
            scored.push({ item, score: value });
        }
    }

    scored.sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        // Same relevance: soonest departure first, then alphabetical.
        if (a.item.d && b.item.d) {
            return a.item.d < b.item.d ? -1 : 1;
        }
        return a.item.t.localeCompare(b.item.t);
    });

    const groups = GROUPS.map((group) => {
        const items = scored
            .filter((entry) => entry.item.k === group.key)
            .slice(0, group.limit)
            .map((entry) => entry.item);

        return { ...group, items, matched: scored.filter((entry) => entry.item.k === group.key).length };
    }).filter((group) => group.items.length > 0);

    return { groups, total: scored.length };
}

/**
 * "Sat 14 Mar 2027" for a YYYY-MM-DD event date.
 *
 * @param {string} value
 * @return {string}
 */
export function formatEventDate(value) {
    if (!value) {
        return '';
    }

    const parts = value.split('-');
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));

    if (isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}
